<script lang="ts" setup>
import { useLanguage } from '#composables/useLanguage';
import { formatIndicatorValue, getNumberOrDefault, secureMathRandom } from '#helpers';
import type { Indicator } from '@carrefour-gcs/shared';
import type { EChartsOption, SeriesOption } from 'echarts';
import { LineChart } from 'echarts/charts';
import { GridComponent, LegendComponent, ToolboxComponent, TooltipComponent } from 'echarts/components';
import { registerTheme, use } from 'echarts/core';
import { UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import { computed, onBeforeMount } from 'vue';
import VChart from 'vue-echarts';
import { LINKS_GROWTH_THEME } from './constants';

const props = defineProps<{
  ordinates: NonNullable<Indicator['ordinates']>;
  abscissae: NonNullable<Indicator['abscissae']>;
}>();

// --- Instantiate composables.
const { t, language } = useLanguage();

// --- Register the echarts components and theme.
onBeforeMount(() => {
  use([
    ToolboxComponent,
    TooltipComponent,
    GridComponent,
    LegendComponent,
    LineChart,
    CanvasRenderer,
    UniversalTransition
  ]);
  registerTheme('linksGrowth', LINKS_GROWTH_THEME);
});

const hasNoData = computed(() =>
  props.ordinates.flatMap((ordinate) => ordinate.values ?? []).every((item) => item === null)
);

const option = computed(() => {
  const legend = props.ordinates.map((value) => t(value.name));
  const unit = props.ordinates[0]?.unit ?? 'integer';
  const unitLabel = props.ordinates[0]?.unitLabel;

  // --- Build the `echarts` series.
  const series = props.ordinates
    .filter((ordinate) => ordinate.values?.length && ordinate.name !== undefined)
    .map<SeriesOption>((ordinate) => ({
      name: t(ordinate.name),
      type: 'line',
      areaStyle: { opacity: 0 },
      emphasis: { focus: 'series' },
      data: ordinate.values?.map((item) => getNumberOrDefault(item, 0)),
      smooth: true,
      animationDuration: 3000,
      animationEasing: 'sinusoidalInOut'
    }));

  // --- Build the `xAxis.data` values.
  const abscissae = props.abscissae.map((value) => t(value));

  // --- Declare the common value formatter.
  const valueFormatter = (value: number) =>
    formatIndicatorValue({
      indicator: { value, unit, unitLabel },
      language: language.value,
      isChart: true
    });

  const noDataSeries = Array.from(
    { length: props.ordinates?.length ? props.ordinates?.length : 3 },
    () => secureMathRandom() * 100
  ).map<SeriesOption>((_, index) => {
    return {
      name: props.ordinates[index] ? t(props.ordinates[index].name) : undefined,
      type: 'line',
      areaStyle: { opacity: 0 },
      emphasis: { focus: 'series' },
      data: Array.from({ length: abscissae.length }, () => secureMathRandom() * 100),
      smooth: true,
      animationDuration: 3000,
      animationEasing: 'sinusoidalInOut'
    };
  });

  return {
    series: hasNoData.value ? noDataSeries : series,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line' },
      valueFormatter
    },
    legend: {
      bottom: 0,
      left: 0,
      data: legend
    },
    grid: {
      top: 15,
      right: 15,
      left: 15,
      bottom: 25,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: abscissae
    },
    yAxis: {
      type: 'value',
      position: 'left',
      axisLabel: {
        formatter: valueFormatter
      }
    }
  } as EChartsOption;
});
</script>

<template>
  <div class="relative">
    <VChart
      class="w-full h-64"
      :class="{
        grayscale: hasNoData
      }"
      :option="option"
      theme="linksGrowth"
      autoresize
    />

    <div v-if="hasNoData" class="absolute top-0 left-0 h-full w-full flex items-center justify-center">
      <div class="bg-white p-5">
        <div class="font-bold">{{ t('chart.noData.title') }}</div>
        <div>{{ t('chart.noData.content') }}</div>
      </div>
    </div>
  </div>
</template>
